import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  UserPlus, Upload, Trash2, RefreshCw, Search, X, Mail, Phone, Globe2,
  Download, Pencil, Tag as TagIcon, AlertTriangle,
} from "lucide-react";
import { formatApiError, mailingApi } from "./api";

const STATUS_COLORS = {
  active: "bg-[#E0D4B6]/40 text-[#4A6741] border-[#E0D4B6]",
  unsubscribed: "bg-[#D4A017]/40 text-[#D4A017] border-[#D4A017]",
  bounced: "bg-[#C0392B]/40 text-[#C0392B] border-[#C0392B]",
  no_email: "bg-[#F0E6CC]/60 text-[#3D1F00]/60 border-[#E0D4B6]",
};

const EXPORT_COLS = [
  "email", "name", "company", "contact1", "contact2",
  "country", "website", "status", "tags", "source",
];

function itemsToCsv(items) {
  const esc = (v) => {
    const s = Array.isArray(v) ? v.join(", ") : v == null ? "" : v;
    return `"${String(s).replace(/"/g, '""')}"`;
  };
  return [
    EXPORT_COLS.join(","),
    ...items.map((c) => EXPORT_COLS.map((k) => esc(c[k])).join(",")),
  ].join("\n");
}

function downloadBlob(data, filename, type = "text/csv") {
  const blob = data instanceof Blob ? data : new Blob([data], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export default function Contacts() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [tagFilter, setTagFilter] = useState("");
  const [search, setSearch] = useState("");
  const [modalContact, setModalContact] = useState(undefined); // undefined=closed, null=add, obj=edit
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(null); // {current,total} | null
  const [exporting, setExporting] = useState(false);
  const [importSummary, setImportSummary] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [showDeleteAll, setShowDeleteAll] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (tagFilter) params.tag = tagFilter;
      const [data, countRes] = await Promise.all([
        mailingApi.contacts.list(params),
        mailingApi.contacts.count(params).catch(() => null),
      ]);
      setItems(data);
      setTotal(countRes?.total ?? data.length);
      setSelected(new Set());
    } catch (err) {
      toast.error(formatApiError(err, "Failed to load contacts"));
    } finally {
      setLoading(false);
    }
  }, [statusFilter, tagFilter]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (!search) return items;
    const q = search.toLowerCase();
    return items.filter((c) =>
      [c.email, c.name, c.company, c.country, c.contact1, c.contact2]
        .some((v) => (v || "").toLowerCase().includes(q)),
    );
  }, [items, search]);

  const tagOptions = useMemo(() => {
    const set = new Set();
    items.forEach((c) => (c.tags || []).forEach((t) => set.add(t)));
    return Array.from(set).sort();
  }, [items]);

  const selectedItems = useMemo(
    () => filtered.filter((c) => selected.has(c.id)),
    [filtered, selected],
  );
  const allFilteredSelected = filtered.length > 0 && filtered.every((c) => selected.has(c.id));

  const toggleOne = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const toggleAll = () =>
    setSelected(() => (allFilteredSelected ? new Set() : new Set(filtered.map((c) => c.id))));

  const clearSelection = () => setSelected(new Set());

  // ---- single-row actions ----
  const onDelete = async (c) => {
    if (!window.confirm(`Delete ${c.email || c.company || "this contact"}?`)) return;
    try {
      await mailingApi.contacts.remove(c.id);
      setItems((cur) => cur.filter((x) => x.id !== c.id));
      setTotal((t) => (t == null ? t : Math.max(0, t - 1)));
      toast.success("Contact deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  // ---- bulk actions ----
  const onBulkDelete = async () => {
    const ids = [...selected];
    if (!ids.length) return;
    if (!window.confirm(`Delete ${ids.length} selected contact${ids.length > 1 ? "s" : ""}? This cannot be undone.`)) return;
    setBulkBusy(true);
    try {
      const res = await mailingApi.contacts.bulkDelete(ids);
      const idSet = new Set(ids);
      setItems((cur) => cur.filter((x) => !idSet.has(x.id)));
      setTotal((t) => (t == null ? t : Math.max(0, t - (res.deleted ?? ids.length))));
      clearSelection();
      toast.success(`Deleted ${res.deleted ?? ids.length} contacts`);
    } catch (err) {
      toast.error(formatApiError(err, "Bulk delete failed"));
    } finally {
      setBulkBusy(false);
    }
  };

  const onBulkStatus = async (status) => {
    const ids = [...selected];
    if (!ids.length || !status) return;
    setBulkBusy(true);
    try {
      await mailingApi.contacts.bulkStatus(ids, status);
      const idSet = new Set(ids);
      setItems((cur) => cur.map((x) => (idSet.has(x.id) ? { ...x, status } : x)));
      toast.success(`Marked ${ids.length} as ${status}`);
    } catch (err) {
      toast.error(formatApiError(err, "Status update failed"));
    } finally {
      setBulkBusy(false);
    }
  };

  const onBulkTag = async () => {
    const ids = [...selected];
    if (!ids.length) return;
    const tag = window.prompt("Add tag to selected contacts:");
    if (!tag || !tag.trim()) return;
    const clean = tag.trim();
    setBulkBusy(true);
    try {
      await mailingApi.contacts.bulkTag(ids, clean);
      const idSet = new Set(ids);
      setItems((cur) => cur.map((x) =>
        idSet.has(x.id) ? { ...x, tags: [...new Set([...(x.tags || []), clean])] } : x));
      toast.success(`Tagged ${ids.length} contacts "${clean}"`);
    } catch (err) {
      toast.error(formatApiError(err, "Tagging failed"));
    } finally {
      setBulkBusy(false);
    }
  };

  const onExportSelected = () => {
    if (!selectedItems.length) return;
    const stamp = new Date().toISOString().slice(0, 16).replace(/[:T]/g, "-");
    downloadBlob(itemsToCsv(selectedItems), `contacts-selected-${stamp}.csv`);
    toast.success(`Exported ${selectedItems.length} contacts`);
  };

  const onExportAll = async () => {
    setExporting(true);
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (tagFilter) params.tag = tagFilter;
      const blob = await mailingApi.contacts.exportCsv(params);
      const stamp = new Date().toISOString().slice(0, 16).replace(/[:T]/g, "-");
      downloadBlob(blob, `contacts-${stamp}.csv`);
      toast.success("Export ready");
    } catch (err) {
      toast.error(formatApiError(err, "Export failed"));
    } finally {
      setExporting(false);
    }
  };

  // ---- import (supports multiple CSV files at once) ----
  const onImport = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setImporting(true);
    setImportProgress({ current: 0, total: files.length });
    const agg = {
      imported: 0, updated: 0, skipped_invalid: 0,
      skipped_unsubscribed: 0, skipped_duplicate: 0, files: files.length, failed: 0,
    };
    try {
      for (let i = 0; i < files.length; i++) {
        setImportProgress({ current: i + 1, total: files.length });
        try {
          const s = await mailingApi.contacts.importCsv(files[i]);
          agg.imported += s.imported || 0;
          agg.updated += s.updated || 0;
          agg.skipped_invalid += s.skipped_invalid || 0;
          agg.skipped_unsubscribed += s.skipped_unsubscribed || 0;
          agg.skipped_duplicate += s.skipped_duplicate || 0;
        } catch (err) {
          agg.failed += 1;
          toast.error(`${files[i].name}: ${formatApiError(err, "import failed")}`);
        }
      }
      setImportSummary(agg);
      const okFiles = files.length - agg.failed;
      toast.success(
        `Imported ${agg.imported}, updated ${agg.updated} from ${okFiles}/${files.length} file${files.length > 1 ? "s" : ""}`,
      );
      load();
    } finally {
      setImporting(false);
      setImportProgress(null);
      e.target.value = "";
    }
  };

  const filterLabel = statusFilter || tagFilter
    ? `matching ${[statusFilter, tagFilter && `tag:${tagFilter}`].filter(Boolean).join(" + ")}`
    : "all contacts";

  return (
    <div>
      {/* Count line */}
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="velora-micro text-[#3D1F00]/50">
          {total != null ? `${total} ${filterLabel}` : "…"}
          {search && filtered.length !== items.length ? ` · ${filtered.length} match "${search}"` : ""}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={onExportAll}
            disabled={exporting || (total === 0)}
            className="velora-btn-ghost flex items-center gap-2 disabled:opacity-50"
            title="Export current view to CSV"
          >
            <Download size={14} /> {exporting ? "Exporting..." : "Export CSV"}
          </button>
          <button
            onClick={() => setShowDeleteAll(true)}
            disabled={!total}
            className="flex items-center gap-2 px-3 py-2 text-sm border border-[#C0392B]/40 text-[#C0392B] hover:bg-[#C0392B]/10 transition disabled:opacity-40"
            title="Delete all contacts in current view"
          >
            <Trash2 size={14} /> Delete all
          </button>
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#3D1F00]/30" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search email, name, company, phone, country..."
            className="velora-input !pl-9 w-full"
          />
        </div>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="velora-input">
          <option value="">All status</option>
          <option value="active">Active</option>
          <option value="no_email">No email</option>
          <option value="unsubscribed">Unsubscribed</option>
          <option value="bounced">Bounced</option>
        </select>
        <select value={tagFilter} onChange={(e) => setTagFilter(e.target.value)} className="velora-input">
          <option value="">All tags</option>
          {tagOptions.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <button onClick={load} className="velora-btn-ghost flex items-center gap-2" title="Refresh">
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
        </button>
        <button onClick={() => setModalContact(null)} className="velora-btn-primary flex items-center gap-2">
          <UserPlus size={14} /> Add
        </button>
        <input ref={fileRef} type="file" accept=".csv,text/csv" multiple onChange={onImport} className="hidden" />
        <button
          onClick={() => fileRef.current?.click()}
          disabled={importing}
          className="velora-btn-primary flex items-center gap-2 disabled:opacity-60"
        >
          <Upload size={14} />
          {importing
            ? (importProgress ? `Importing ${importProgress.current}/${importProgress.total}...` : "Importing...")
            : "Import CSV"}
        </button>
      </div>

      {importSummary && (
        <div className="border border-[#E0D4B6] bg-[#FFFFFF] p-4 mb-6 flex items-start gap-4">
          <div className="flex-1 grid grid-cols-2 md:grid-cols-4 gap-4">
            <SummaryStat label="Imported" value={importSummary.imported} />
            <SummaryStat label="Updated" value={importSummary.updated} />
            <SummaryStat label="Invalid" value={importSummary.skipped_invalid} />
            <SummaryStat label="Skipped (unsub)" value={importSummary.skipped_unsubscribed} />
          </div>
          <button onClick={() => setImportSummary(null)} className="text-[#3D1F00]/40 hover:text-[#7B3F00]">
            <X size={16} />
          </button>
        </div>
      )}

      {/* Bulk action bar */}
      {selected.size > 0 && (
        <div className="sticky top-0 z-20 flex flex-wrap items-center gap-3 mb-4 px-4 py-3 bg-[#7B3F00] text-[#FAF3E0] shadow-lg">
          <span className="text-sm font-medium">{selected.size} selected</span>
          <div className="h-4 w-px bg-[#FAF3E0]/30" />
          <button onClick={onBulkDelete} disabled={bulkBusy} className="flex items-center gap-1.5 text-sm hover:text-white disabled:opacity-50">
            <Trash2 size={14} /> Delete
          </button>
          <button onClick={onBulkTag} disabled={bulkBusy} className="flex items-center gap-1.5 text-sm hover:text-white disabled:opacity-50">
            <TagIcon size={14} /> Add tag
          </button>
          <select
            onChange={(e) => { if (e.target.value) { onBulkStatus(e.target.value); e.target.value = ""; } }}
            disabled={bulkBusy}
            defaultValue=""
            className="bg-[#7B3F00] border border-[#FAF3E0]/40 text-[#FAF3E0] text-sm px-2 py-1 outline-none"
          >
            <option value="" disabled>Set status…</option>
            <option value="active">Active</option>
            <option value="unsubscribed">Unsubscribed</option>
            <option value="bounced">Bounced</option>
          </select>
          <button onClick={onExportSelected} disabled={bulkBusy} className="flex items-center gap-1.5 text-sm hover:text-white disabled:opacity-50">
            <Download size={14} /> Export
          </button>
          <button onClick={clearSelection} className="ml-auto flex items-center gap-1.5 text-sm hover:text-white">
            <X size={14} /> Clear
          </button>
        </div>
      )}

      {/* Table */}
      <div className="border border-[#E0D4B6] bg-[#FAF3E0] overflow-hidden">
        <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-3 border-b border-[#E0D4B6] velora-micro text-[#3D1F00]/40 items-center">
          <div className="col-span-1 flex items-center">
            <input
              type="checkbox"
              checked={allFilteredSelected}
              onChange={toggleAll}
              className="accent-[#7B3F00] w-4 h-4 cursor-pointer"
              title="Select all"
            />
          </div>
          <div className="col-span-3">Email</div>
          <div className="col-span-3">Company / Name</div>
          <div className="col-span-2">Phone</div>
          <div className="col-span-2">Website</div>
          <div className="col-span-1 text-right">Status</div>
        </div>
        {loading ? (
          <div className="p-12 text-center velora-micro text-[#3D1F00]/40">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center velora-micro text-[#3D1F00]/50">No contacts.</div>
        ) : (
          filtered.map((c) => (
            <ContactRow
              key={c.id}
              contact={c}
              selected={selected.has(c.id)}
              onToggle={() => toggleOne(c.id)}
              onDelete={onDelete}
              onEdit={() => setModalContact(c)}
            />
          ))
        )}
      </div>

      {modalContact !== undefined && (
        <ContactModal
          contact={modalContact}
          onClose={() => setModalContact(undefined)}
          onSaved={(saved, isEdit) => {
            setItems((cur) => isEdit
              ? cur.map((x) => (x.id === saved.id ? saved : x))
              : [saved, ...cur]);
            if (!isEdit) setTotal((t) => (t == null ? t : t + 1));
            setModalContact(undefined);
          }}
        />
      )}

      {showDeleteAll && (
        <DeleteAllModal
          count={total}
          filterLabel={filterLabel}
          onClose={() => setShowDeleteAll(false)}
          onConfirm={async () => {
            setBulkBusy(true);
            try {
              const res = await mailingApi.contacts.deleteAll({
                status: statusFilter || null,
                tag: tagFilter || null,
              });
              toast.success(`Deleted ${res.deleted} contacts`);
              setShowDeleteAll(false);
              load();
            } catch (err) {
              toast.error(formatApiError(err, "Delete all failed"));
            } finally {
              setBulkBusy(false);
            }
          }}
          busy={bulkBusy}
        />
      )}
    </div>
  );
}

function SummaryStat({ label, value }) {
  return (
    <div>
      <div className="velora-micro text-[#3D1F00]/40">{label}</div>
      <div className="velora-serif text-2xl text-[#7B3F00] mt-1">{value}</div>
    </div>
  );
}

function ContactRow({ contact, selected, onToggle, onDelete, onEdit }) {
  const c = contact;
  const cls = STATUS_COLORS[c.status] || STATUS_COLORS.active;
  const websiteHref = c.website
    ? (c.website.startsWith("http") ? c.website : `https://${c.website}`)
    : null;
  const websiteLabel = c.website ? c.website.replace(/^https?:\/\//, "").replace(/\/$/, "") : "";
  const primaryLabel = c.company || c.name || "-";
  const secondaryLabel = c.company && c.name ? c.name : (c.country || "");
  return (
    <motion.div
      layout
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className={`border-b border-[#E0D4B6]/50 last:border-b-0 transition-colors ${selected ? "bg-[#7B3F00]/5" : "hover:bg-[#FFFFFF]/50"}`}
    >
      <div className="md:grid md:grid-cols-12 gap-4 px-6 py-4 items-center">
        {/* Select */}
        <div className="col-span-1 flex items-center gap-3 mb-2 md:mb-0">
          <input
            type="checkbox"
            checked={selected}
            onChange={onToggle}
            className="accent-[#7B3F00] w-4 h-4 cursor-pointer"
          />
        </div>
        {/* Email */}
        <div className="col-span-3 flex items-center gap-2 min-w-0">
          <Mail size={12} className={`shrink-0 ${c.email ? "text-[#7B3F00]/60" : "text-[#3D1F00]/20"}`} />
          {c.email ? (
            <a href={`mailto:${c.email}`} className="text-sm truncate hover:text-[#7B3F00] transition-colors" title={c.email}>
              {c.email}
            </a>
          ) : (
            <span className="text-sm italic text-[#3D1F00]/30">no email</span>
          )}
        </div>
        {/* Company / Name */}
        <div className="col-span-3 min-w-0">
          <div className="text-sm text-[#3D1F00]/85 truncate" title={primaryLabel}>{primaryLabel}</div>
          {secondaryLabel && <div className="velora-micro text-[#3D1F00]/40 truncate">{secondaryLabel}</div>}
          {(c.tags || []).length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1">
              {c.tags.slice(0, 3).map((t) => (
                <span key={t} className="text-[9px] uppercase tracking-wide px-1.5 py-0.5 bg-[#E0D4B6]/50 text-[#7B3F00] border border-[#E0D4B6]">{t}</span>
              ))}
              {c.tags.length > 3 && <span className="text-[9px] text-[#3D1F00]/40">+{c.tags.length - 3}</span>}
            </div>
          )}
        </div>
        {/* Phone */}
        <div className="col-span-2 min-w-0 text-sm">
          {c.contact1 || c.contact2 ? (
            <div className="flex items-start gap-2 min-w-0">
              <Phone size={12} className="text-[#7B3F00]/60 shrink-0 mt-1" />
              <div className="min-w-0">
                {c.contact1 && (
                  <a href={`tel:${c.contact1.replace(/\s+/g, "")}`} className="block text-[#3D1F00]/80 hover:text-[#7B3F00] truncate" title={c.contact1}>{c.contact1}</a>
                )}
                {c.contact2 && (
                  <a href={`tel:${c.contact2.replace(/\s+/g, "")}`} className="block velora-micro text-[#3D1F00]/45 hover:text-[#7B3F00] truncate" title={c.contact2}>{c.contact2}</a>
                )}
              </div>
            </div>
          ) : (
            <span className="text-[#3D1F00]/30">-</span>
          )}
        </div>
        {/* Website */}
        <div className="col-span-2 min-w-0 text-sm">
          {websiteHref ? (
            <a href={websiteHref} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-[#3D1F00]/70 hover:text-[#7B3F00] truncate" title={c.website}>
              <Globe2 size={12} className="text-[#7B3F00]/60 shrink-0" />
              <span className="truncate">{websiteLabel}</span>
            </a>
          ) : (
            <span className="text-[#3D1F00]/30">-</span>
          )}
        </div>
        {/* Status + actions */}
        <div className="col-span-1 flex items-center justify-end gap-2">
          <span className={`inline-block text-[10px] uppercase tracking-wider px-2 py-1 border ${cls}`}>{c.status}</span>
          <button onClick={onEdit} className="text-[#3D1F00]/30 hover:text-[#7B3F00]" title="Edit contact"><Pencil size={12} /></button>
          <button onClick={() => onDelete(c)} className="text-[#3D1F00]/30 hover:text-[#C0392B]" title="Delete contact"><Trash2 size={12} /></button>
        </div>
      </div>
    </motion.div>
  );
}

function ContactModal({ contact, onClose, onSaved }) {
  const isEdit = !!contact;
  const [form, setForm] = useState({
    email: contact?.email || "",
    name: contact?.name || "",
    company: contact?.company || "",
    contact1: contact?.contact1 || "",
    contact2: contact?.contact2 || "",
    country: contact?.country || "",
    website: contact?.website || "",
    tags: (contact?.tags || []).join(", "),
    status: contact?.status || "active",
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email && !form.company && !form.contact1) {
      return toast.error("Provide at least an email, company name, or phone");
    }
    setSaving(true);
    try {
      const tags = form.tags.split(",").map((t) => t.trim()).filter(Boolean);
      const payload = { ...form, tags };
      if (!payload.email) {
        if (isEdit) payload.email = null; else delete payload.email;
      }
      if (isEdit) {
        const saved = await mailingApi.contacts.update(contact.id, payload);
        onSaved(saved, true);
        toast.success("Contact updated");
      } else {
        const created = await mailingApi.contacts.create(payload);
        onSaved(created, false);
        toast.success("Contact added");
      }
    } catch (err) {
      toast.error(formatApiError(err, isEdit ? "Update failed" : "Failed to add contact"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-lg bg-[#FFFFFF] border border-[#E0D4B6] p-8 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="velora-micro text-[#7B3F00]">{isEdit ? "Edit Contact" : "New Contact"}</div>
            <div className="velora-serif text-2xl text-[#3D1F00] mt-1">{isEdit ? (contact.company || contact.email || "Edit") : "Add buyer to list"}</div>
          </div>
          <button onClick={onClose} className="text-[#3D1F00]/40 hover:text-[#7B3F00]"><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <Field label="Email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} type="email" />
          <Field label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
          <Field label="Company" value={form.company} onChange={(v) => setForm({ ...form, company: v })} />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Contact 1" value={form.contact1} onChange={(v) => setForm({ ...form, contact1: v })} />
            <Field label="Contact 2" value={form.contact2} onChange={(v) => setForm({ ...form, contact2: v })} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Country" value={form.country} onChange={(v) => setForm({ ...form, country: v })} />
            <Field label="Website" value={form.website} onChange={(v) => setForm({ ...form, website: v })} />
          </div>
          <Field label="Tags (comma-separated)" value={form.tags} onChange={(v) => setForm({ ...form, tags: v })} />
          {isEdit && (
            <div>
              <label className="velora-micro text-[#3D1F00]/50 block mb-1">Status</label>
              <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="velora-input w-full">
                <option value="active">Active</option>
                <option value="no_email">No email</option>
                <option value="unsubscribed">Unsubscribed</option>
                <option value="bounced">Bounced</option>
              </select>
            </div>
          )}
          <button type="submit" disabled={saving} className="velora-btn-primary w-full justify-center disabled:opacity-50">
            {saving ? "Saving..." : isEdit ? "Save changes" : "Add Contact"}
          </button>
        </form>
      </div>
    </div>
  );
}

function DeleteAllModal({ count, filterLabel, onClose, onConfirm, busy }) {
  const [confirmText, setConfirmText] = useState("");
  const ready = confirmText.trim().toUpperCase() === "DELETE";
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md bg-[#FFFFFF] border border-[#C0392B]/40 p-8">
        <div className="flex items-center gap-3 mb-4 text-[#C0392B]">
          <AlertTriangle size={22} />
          <div className="velora-serif text-2xl">Delete all contacts</div>
        </div>
        <p className="text-sm text-[#3D1F00]/70 mb-2">
          This permanently deletes <strong>{count ?? "all"} contacts</strong> ({filterLabel}). This cannot be undone.
        </p>
        <p className="velora-micro text-[#3D1F00]/50 mb-4">Type <strong>DELETE</strong> to confirm.</p>
        <input
          value={confirmText}
          onChange={(e) => setConfirmText(e.target.value)}
          placeholder="DELETE"
          className="velora-input w-full mb-5"
          autoFocus
        />
        <div className="flex gap-3 justify-end">
          <button onClick={onClose} className="velora-btn-ghost">Cancel</button>
          <button
            onClick={onConfirm}
            disabled={!ready || busy}
            className="flex items-center gap-2 px-4 py-2 text-sm bg-[#C0392B] text-white hover:bg-[#a93226] transition disabled:opacity-40"
          >
            {busy ? "Deleting..." : <><Trash2 size={14} /> Delete all</>}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required = false }) {
  return (
    <div>
      <label className="velora-micro text-[#3D1F00]/50 block mb-1">{label}</label>
      <input type={type} required={required} value={value} onChange={(e) => onChange(e.target.value)} className="velora-input w-full" />
    </div>
  );
}
